import React, { useMemo, useState } from 'react';
import { max } from 'lodash';
import { CartesianGrid, DotItemDotProps, Line, LineChart, XAxis } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RotateCw } from 'lucide-react';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { fmtEur, formatNumberToDate } from '../../utils/formatArcaNumbers';
import { PlanValuePointI } from '../../api/users/getPlansValueHistory';
import { SingleUserContributionData } from '../../api/users/interfaces/ContributionData';
import { Rentabilidad } from '../../utils/resumenMulticuenta/rentabilidad';
import { fmtPorcentaje } from '../../utils/resumenMulticuenta/formato';
import {
	DESDE_EL_INICIO,
	FilaEvolucion,
	PeriodoEvolucion,
	PuntoEvolucion,
	conMesesSeguidos,
	esPuntoUnico,
	mesDeMadrid,
	serieDelAnio,
	serieDesdeElInicio,
	serieEnEuros
} from '../../utils/resumenMulticuenta/evolucion';

/** Lo que la gráfica necesita de UN plan. */
export interface PlanEvolucion {
	id: string;
	nombre: string;
	/** Alta del plan (`createdAt`). */
	creadoEn: string;
	/** `GET /users/plans/value-history` de este plan. */
	puntos: PlanValuePointI[];
	/** `null` si no llegaron: sin ellos no hay %, pero sí €. */
	movimientos: SingleUserContributionData[] | null;
	/** La acumulada de la card. `undefined` mientras carga: no manda. */
	rentabilidad?: Rentabilidad | null;
	/** La del año de la card. `undefined` mientras carga: no manda. */
	rentabilidadAnio?: Rentabilidad | null;
	/** El saldo (€) y el día de partida de la card, de `GET /users/plans`: ver `serieDelAnio`. */
	partidaDelAnioEnCurso?: { saldo: number | null; fecha: string | null };
}

export interface EvolucionPlanesProps {
	planes: PlanEvolucion[];
	/** Los planes del filtro: los demás se atenúan, no se quitan (Proa). */
	elegidos: string[];
	/** El periodo del selector de año de arriba (`FiltroPlanes`): `'todo'` o un año. */
	periodo: PeriodoEvolucion;
	/** Cómo fue `GET /users/plans/value-history`. */
	estado: 'cargando' | 'error' | 'listo';
	hoy: Date;
	language: string;
	translate: (clave: string) => string;
	/** Vuelve a pedir la serie. Sin él, el aviso de error va sin botón. */
	onReintentar?: () => void;
}

type Unidad = 'pct' | 'eur';

/** Los grises verdosos de Proa (`PLAN_SHADES`), los mismos de la barra del año. */
const TONOS = ['#1F3328', '#3D5C49', '#6C8A78', '#A3BBAD'];
/**
 * El trazo de cada plan, porque el tono solo no basta para distinguirlos (Proa: «nunca el hue
 * para diferenciar planes»). El primero, continuo; su equivalente en la leyenda, al lado.
 */
const TRAZOS: (string | undefined)[] = [undefined, '6 3', '2 2'];
const TRAZOS_LEYENDA = ['solid', 'dashed', 'dotted'];
const OPACIDAD_ATENUADA = 0.12;
/** El alto de la gráfica (el de Proa): lo guardan también el skeleton y «Sin dato». */
const ALTO_GRAFICA = 'h-[190px]';
/** Una línea de leyenda (`text-xs`, 16 px): la reservan también el skeleton y los avisos. */
const ALTO_LEYENDA = 'h-4';
/** La línea de «Valor a» bajo la leyenda (`text-xs`, 16 px): reservada siempre, haya fecha o no. */
const ALTO_VALOR_A = 'h-4';

interface Serie {
	plan: PlanEvolucion;
	indice: number;
	puntos: PuntoEvolucion[];
	desde: string | null;
}

/**
 * «Evolución de tus planes» del Resumen multicuenta (chirigota#613), siguiendo `EvolutionChart` de
 * Proa: una línea por plan, en % o en €, para un año o desde el inicio, con los planes fuera del
 * filtro atenuados.
 *
 * - €: el valor de cada mes (la foto de Caser de ESTA persona, no el valor liquidativo del fondo).
 * - % de un año: la rentabilidad del año hasta cada mes, la cuenta de la card.
 * - % desde el inicio: la acumulada sobre lo metido de cada mes (product#47).
 * El último punto del año en curso, y el de desde el inicio, son las cifras de la card.
 *
 * El periodo (un año o desde el inicio) lo elige el selector de arriba, junto al filtro de planes,
 * como en Proa: los dos afectan a todo lo de debajo (Marisa, 21/10/2026).
 */
const EvolucionPlanes: React.FC<EvolucionPlanesProps> = ({
	planes,
	elegidos,
	periodo,
	estado,
	hoy,
	language,
	translate,
	onReintentar
}) => {
	const [unidad, setUnidad] = useState<Unidad>('pct');
	const locale = language === 'en' ? 'en-US' : 'es-ES';
	const mesLegible = (mes: string) =>
		new Date(`${mes}-15T12:00:00Z`).toLocaleDateString(locale, {
			month: 'short',
			year: 'numeric',
			timeZone: 'Europe/Madrid'
		});

	const series: Serie[] = useMemo(
		() =>
			planes
				.map((plan, indice): Serie => {
					if (unidad === 'eur') {
						return { plan, indice, puntos: serieEnEuros(plan.puntos, periodo), desde: null };
					}
					if (!plan.movimientos) return { plan, indice, puntos: [], desde: null };
					if (periodo === DESDE_EL_INICIO) {
						return {
							plan,
							indice,
							...serieDesdeElInicio({
								puntos: plan.puntos,
								movimientos: plan.movimientos,
								hoy,
								actual: plan.rentabilidad
							})
						};
					}
					return {
						plan,
						indice,
						puntos: serieDelAnio({
							puntos: plan.puntos,
							anio: periodo,
							movimientos: plan.movimientos,
							creadoEn: plan.creadoEn,
							hoy,
							actual: plan.rentabilidadAnio,
							partidaDelAnioEnCurso: plan.partidaDelAnioEnCurso
						}),
						desde: null
					};
				})
				.filter((serie) => serie.puntos.length > 0),
		[planes, unidad, periodo, hoy]
	);

	// Una fila por mes, con una columna por plan. Un plan sin punto ese mes no lleva columna (nunca
	// un 0), y los meses sin punto de nadie van como filas vacías, para que ocupen su sitio. La línea
	// los cruza (`connectNulls`): un hueco quedaba raro (Marisa, 21/10/2026).
	const filas = useMemo(() => {
		const porMes = new Map<string, FilaEvolucion>();
		series.forEach(({ plan, puntos }) =>
			puntos.forEach(({ mes, valor }) => {
				porMes.set(mes, { ...(porMes.get(mes) ?? { mes }), [plan.id]: valor } as FilaEvolucion);
			})
		);
		return conMesesSeguidos(Array.from(porMes.values()).sort((a, b) => a.mes.localeCompare(b.mes)));
	}, [series]);

	const config: ChartConfig = Object.fromEntries(
		series.map(({ plan, indice }) => [
			plan.id,
			{ label: plan.nombre, color: TONOS[indice % TONOS.length] }
		])
	);
	const visible = (plan: PlanEvolucion) => elegidos.includes(plan.id);

	// De qué día es el valor del mes en curso (product#75): el `date` de su punto, el más reciente
	// entre los planes elegidos que se dibujan (uno sin línea no pone su fecha). Solo si el periodo
	// incluye el mes en curso; un año cerrado no lo lleva.
	const mesEnCurso = mesDeMadrid(hoy);
	const incluyeElMesEnCurso = periodo === DESDE_EL_INICIO || mesEnCurso.startsWith(`${periodo}-`);
	const fechasDelMesEnCurso = series
		.filter(({ plan }) => visible(plan))
		.flatMap(({ plan }) => plan.puntos.filter((p) => p.month === mesEnCurso).map((p) => p.date));
	const fechaDelMesEnCurso = incluyeElMesEnCurso ? max(fechasDelMesEnCurso) : undefined;
	const etiqueta = ({ plan, desde }: Serie) =>
		desde
			? `${plan.nombre} (${translate('resumen.chart.since').replace('{mes}', mesLegible(desde))})`
			: plan.nombre;
	const formatear = (valor: number) =>
		unidad === 'pct' ? fmtPorcentaje(valor, language) : fmtEur(valor);

	// Cargando, sin datos y error se distinguen (Marisa, 21/10/2026): con un solo «Sin dato» no se
	// sabía si había que esperar, si no había nada o si había fallado. Los tres guardan el alto de la
	// gráfica y de una línea de leyenda, igual que la gráfica cargada: al llegar los datos no se mueve
	// nada de la pantalla (Marisa, 21/10/2026).
	const aviso = (texto: string, accion?: React.ReactNode) => (
		<>
			<div
				data-testid="evolucion-aviso"
				className={`mt-3 flex flex-col items-center justify-center gap-3 text-center ${ALTO_GRAFICA}`}
			>
				<p className="text-sm text-muted-foreground">{texto}</p>
				{accion}
			</div>
			<div
				data-testid="evolucion-aviso-leyenda"
				className={`mt-3 ${ALTO_LEYENDA}`}
				aria-hidden="true"
			/>
		</>
	);
	const sinDatos = translate('resumen.chart.empty')
		.replace(
			'{unidad}',
			translate(unidad === 'pct' ? 'resumen.chart.emptyUnitPct' : 'resumen.chart.emptyUnitEur')
		)
		.replace(
			'{periodo}',
			periodo === DESDE_EL_INICIO
				? translate('resumen.chart.emptySinceStart')
				: translate('resumen.chart.emptyYear').replace('{anio}', periodo)
		);

	let contenido: React.ReactNode;
	if (estado === 'cargando') {
		// El hueco de la gráfica y su leyenda, con su alto: al llegar los datos no salta nada.
		contenido = (
			<div data-testid="evolucion-cargando" aria-busy="true" role="status">
				<div className="relative">
					<Skeleton
						data-testid="evolucion-cargando-grafica"
						className={`mt-3 w-full ${ALTO_GRAFICA}`}
					/>
					<p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
						{translate('resumen.chart.loading')}
					</p>
				</div>
				<Skeleton
					data-testid="evolucion-cargando-leyenda"
					className={`mt-3 w-40 ${ALTO_LEYENDA}`}
				/>
			</div>
		);
	} else if (estado === 'error') {
		contenido = aviso(
			translate('resumen.chart.error'),
			onReintentar && (
				<Button variant="secondary" size="sm" onClick={onReintentar}>
					<RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
					{translate('resumen.chart.retry')}
				</Button>
			)
		);
	} else if (series.length === 0) {
		contenido = aviso(sinDatos);
	} else {
		contenido = (
			<>
				<ChartContainer
					config={config}
					className={`mt-3 aspect-auto w-full ${ALTO_GRAFICA}`}
					role="img"
					aria-label={translate('resumen.chart.title')}
				>
					<LineChart data={filas} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
						<CartesianGrid vertical={false} />
						<XAxis dataKey="mes" hide />
						<ChartTooltip
							content={
								<ChartTooltipContent
									labelFormatter={(mes) => mesLegible(String(mes))}
									formatter={(valor, nombre) => (
										<div className="flex w-full items-center gap-2">
											<span
												className="h-2.5 w-2.5 shrink-0 rounded-full"
												style={{ backgroundColor: config[String(nombre)]?.color }}
												aria-hidden="true"
											/>
											<span className="flex-1 truncate text-muted-foreground">
												{config[String(nombre)]?.label}
											</span>
											<span className="font-medium text-foreground">
												{formatear(Number(valor))}
											</span>
										</div>
									)}
								/>
							}
						/>
						{series.map(({ plan, indice }) => (
							<Line
								key={plan.id}
								dataKey={plan.id}
								stroke={TONOS[indice % TONOS.length]}
								strokeWidth={2}
								strokeDasharray={TRAZOS[indice % TRAZOS.length]}
								opacity={visible(plan) ? 1 : OPACIDAD_ATENUADA}
								// Sin puntos en la línea, salvo el único dato de un plan: una línea de uno no se ve.
								dot={({ index, cx, cy }: DotItemDotProps) =>
									esPuntoUnico(filas, index, plan.id) ? (
										<circle
											key={`${plan.id}-${index}`}
											cx={cx}
											cy={cy}
											r={3}
											fill={TONOS[indice % TONOS.length]}
										/>
									) : (
										<g key={`${plan.id}-${index}`} />
									)
								}
								connectNulls
								isAnimationActive={false}
							/>
						))}
					</LineChart>
				</ChartContainer>
				<ul className="mt-3 flex min-h-4 flex-wrap gap-4">
					{series.map((serie) => (
						<li
							key={serie.plan.id}
							className={cn(
								'flex max-w-[220px] items-center gap-1.5 text-xs',
								!visible(serie.plan) && 'opacity-30'
							)}
						>
							<span
								className="h-0 w-4 flex-none border-t-2"
								style={{
									borderColor: TONOS[serie.indice % TONOS.length],
									borderStyle: TRAZOS_LEYENDA[serie.indice % TRAZOS_LEYENDA.length]
								}}
								aria-hidden="true"
							/>
							<span className="truncate" title={etiqueta(serie)}>
								{etiqueta(serie)}
							</span>
						</li>
					))}
				</ul>
			</>
		);
	}

	return (
		<div>
			<div className="flex flex-wrap items-center justify-between gap-2">
				{/* «Valor a» va justo debajo del título, y las dos líneas se centran en vertical con las
				    pestañas (Marisa, 30/10/2026). Su línea ocupa siempre su alto, también cargando, con un
				    aviso o con un año cerrado, aunque no lleve fecha: nada se mueve al cargar ni al cambiar
				    de periodo. */}
				<div>
					<p className="text-sm font-medium text-foreground">{translate('resumen.chart.title')}</p>
					<div data-testid="evolucion-linea-valor-a" className={ALTO_VALOR_A}>
						{estado === 'listo' && fechaDelMesEnCurso && (
							<p data-testid="evolucion-valor-a" className="text-xs text-muted-foreground">
								{translate('benefit.hero.valueAsOf')}{' '}
								{formatNumberToDate(Number(fechaDelMesEnCurso.replace(/-/g, '')))}
							</p>
						)}
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Tabs value={unidad} onValueChange={(valor) => setUnidad(valor as Unidad)}>
						<TabsList aria-label={translate('resumen.chart.unit')}>
							<TabsTrigger value="pct">{translate('resumen.chart.percent')}</TabsTrigger>
							<TabsTrigger value="eur">{translate('resumen.chart.euros')}</TabsTrigger>
						</TabsList>
						{/* Vacíos a propósito, como en Proa: sin ellos, el `aria-controls` de cada pestaña
						    apunta a un id que no existe (axe). La gráfica va fuera de las pestañas. */}
						<TabsContent value="pct" />
						<TabsContent value="eur" />
					</Tabs>
				</div>
			</div>
			{contenido}
		</div>
	);
};

export default EvolucionPlanes;
